// @charitystack/org-engagement-contracts — paragon module, HTTP response bodies.
//
// The response bodies of all SIX paragon-backend routes (across FIVE separate
// legacy SST `Api` constructs), transcribed bug-for-bug from the executable
// legacy contract (`contracts-legacy/paragon-backend/schemas.ts`, pinned
// `origin/dev` = `7cd48d532bf395c1313aa1267b113907ac8e0d57`). Every schema is
// `.strict()`. Status-code/headers envelope variants and the gateway-generated
// responses are parity-recorder machinery and are deliberately NOT transcribed.
//
// Two pinned quirks the dashboard is built around (do NOT "fix" here):
//   - `GET /merchant-settings` has NO success body — the legacy route is bound
//     to the wrong handler and returns 500 on every call; the only
//     handler-produced body it can emit is the zapier `except` literal.
//   - `POST /virtuous-webhook`'s success body is DOUBLE-WRAPPED (a
//     `{statusCode, body}` object serialized INTO the real envelope's body).

import { z } from 'zod';

import {
  COMPACT_JWS,
  PARAGON_PROJECT_ID_RE,
  uncoercedCallerValueSchema,
} from './shared.js';

// ---------------------------------------------------------------------------
// Route 1 — GET /get-jwt (DashboardApi, dualAuth)
// ---------------------------------------------------------------------------

/** `json.dumps({'jwt': token, 'projectID': PARAGON_PROJECT_ID})` — the literal has two keys. */
export const getJwtSuccessBodySchema = z
  .object({
    jwt: z.string().regex(COMPACT_JWS),
    projectID: z.string().regex(PARAGON_PROJECT_ID_RE),
  })
  .strict();
export type GetJwtSuccessBody = z.infer<typeof getJwtSuccessBodySchema>;

export const getJwtNoGroupsBodySchema = z
  .object({ error: z.literal('No groups found') })
  .strict();
export type GetJwtNoGroupsBody = z.infer<typeof getJwtNoGroupsBodySchema>;

// ---------------------------------------------------------------------------
// Route 2 — POST /virtuous-webhook (PUBLIC; bearer secret in the query string)
// ---------------------------------------------------------------------------

/**
 * The final success body of `virtuous_webhook.py:handler`, inside the double wrap.
 * The four echoed request fields are read with `.get()` and passed to `json.dumps`
 * with NO coercion anywhere in the file — each is the uncoerced union, not a
 * narrowed type. `event_type` reaches this body for ANY unrecognised value, and
 * the route is PUBLIC, so the value is attacker-controlled. `x_secret_key` is the
 * caller's own shared secret, echoed back (legacy finding F2) — it comes from
 * `event['queryStringParameters']`, and API Gateway query-string values are always
 * strings; a null secret cannot reach this body (it returns 404 earlier).
 */
export const virtuousWebhookInnerBodySchema = z
  .object({
    message: z.literal('Webhook received successfully'),
    // `virtuous_webhook.py:179` — `body.get('event', '')`, uncoerced.
    event_type: uncoercedCallerValueSchema,
    // `virtuous_webhook.py:181` — `project.get('id', None)`, uncoerced.
    project_id: uncoercedCallerValueSchema,
    // `virtuous_webhook.py:182` — `project.get('name', None)`, uncoerced.
    project_name: uncoercedCallerValueSchema,
    // `virtuous_webhook.py:183` — `project.get('isActive', None)`, uncoerced;
    // `"isActive": 1` is truthy, takes the update branch, and is echoed as `1`.
    project_is_active: uncoercedCallerValueSchema,
    // NOT uncoerced: from the query string; `null` is impossible on this path.
    x_secret_key: z.string(),
  })
  .strict();
export type VirtuousWebhookInnerBody = z.infer<typeof virtuousWebhookInnerBodySchema>;

/**
 * The DOUBLE-WRAP: the handler builds a full `{statusCode: 200, body: {...}}` dict
 * and `json.dumps`es it INTO the real envelope's body — an inner `statusCode` that
 * is data, not protocol. Pinned bug-for-bug; the legacy `x-deviations` D3 proposes
 * unwrapping it after cutover.
 */
export const virtuousWebhookDoubleWrappedSchema = z
  .object({
    statusCode: z.literal(200),
    body: virtuousWebhookInnerBodySchema,
  })
  .strict();
export type VirtuousWebhookDoubleWrapped = z.infer<typeof virtuousWebhookDoubleWrappedSchema>;

/** `ORGANIZATIONS_TABLE` env var empty — a real return site, never fired in prod. */
export const virtuousMisconfiguredBodySchema = z
  .object({ error: z.literal('Server misconfiguration: ORGANIZATIONS_TABLE not set.') })
  .strict();
export type VirtuousMisconfiguredBody = z.infer<typeof virtuousMisconfiguredBodySchema>;

/** The paginated secret-key scan matched no org. */
export const virtuousNoMerchantBodySchema = z
  .object({ error: z.literal('No merchant found for the provided x-secret-key.') })
  .strict();
export type VirtuousNoMerchantBody = z.infer<typeof virtuousNoMerchantBodySchema>;

/** `project_id == merchant['virtuousProcessingFeeId']` — returns before any write. */
export const virtuousFeeProjectIgnoredBodySchema = z
  .object({ message: z.literal('No action taken. Project ID matches virtuousProcessingFeeId.') })
  .strict();
export type VirtuousFeeProjectIgnoredBody = z.infer<typeof virtuousFeeProjectIgnoredBodySchema>;

/** `GiftCreate` whose `gift.transactionId` is falsy — the HANDLER's early return. */
export const virtuousGiftSkippedBodySchema = z
  .object({ message: z.literal('Skipping storage - no Virtuous transaction ID provided') })
  .strict();
export type VirtuousGiftSkippedBody = z.infer<typeof virtuousGiftSkippedBodySchema>;

// ---------------------------------------------------------------------------
// Route 3 — POST /default-segment (dualAuth)
// ---------------------------------------------------------------------------

export const defaultSegmentUnauthorizedBodySchema = z
  .object({ error: z.literal('Unauthorized') })
  .strict();
export type DefaultSegmentUnauthorizedBody = z.infer<typeof defaultSegmentUnauthorizedBodySchema>;

export const defaultSegmentBadRequestBodySchema = z
  .object({ error: z.literal('virtuousSegmentId is required') })
  .strict();
export type DefaultSegmentBadRequestBody = z.infer<typeof defaultSegmentBadRequestBodySchema>;

export const defaultSegmentSuccessBodySchema = z
  .object({ message: z.literal('DynamoDB update successful.') })
  .strict();
export type DefaultSegmentSuccessBody = z.infer<typeof defaultSegmentSuccessBodySchema>;

// ---------------------------------------------------------------------------
// Route 4 — POST /paragon-logs (PUBLIC, no authentication at all)
// ---------------------------------------------------------------------------

export const paragonLogsSuccessBodySchema = z
  .object({ message: z.literal('Paragon Logs Function Executed Successfully') })
  .strict();
export type ParagonLogsSuccessBody = z.infer<typeof paragonLogsSuccessBodySchema>;

// ---------------------------------------------------------------------------
// Route 5 — POST /zapier-webhook-url (dualAuth)
// ---------------------------------------------------------------------------

/**
 * `{"message": "Internal Server Error", "error": str(e)}`. `str(e)` is unconstrained —
 * for a bare `KeyError` it is the quoted key name, e.g. `"'body'"` — so `error` is
 * `z.string()`, deliberately not a pattern: narrowing it would reject legitimate
 * legacy responses. This is ALSO the only handler-produced body of
 * `GET /merchant-settings` (both routes bind the same handler; see the file head).
 */
export const zapierInternalErrorSchema = z
  .object({ message: z.literal('Internal Server Error'), error: z.string() })
  .strict();
export type ZapierInternalError = z.infer<typeof zapierInternalErrorSchema>;

export const zapierMerchantRequiredBodySchema = z
  .object({ message: z.literal("'merchantName' is required") })
  .strict();
export type ZapierMerchantRequiredBody = z.infer<typeof zapierMerchantRequiredBodySchema>;

export const zapierNothingToUpdateBodySchema = z
  .object({ message: z.literal('Nothing to update.') })
  .strict();
export type ZapierNothingToUpdateBody = z.infer<typeof zapierNothingToUpdateBodySchema>;

/**
 * The `results[]` element of the zapier test-event 200. Built by exactly two `append`
 * sites that are structurally DISJOINT: success → `{url, success: true, status_code}`
 * (no `error` key); failure → `{url, success: false, error}` (no `status_code` key).
 * Modelled as a discriminated union so a body carrying both keys, or neither, is
 * rejected. `url` is UNCOERCED caller input — `{"zapierWebhookURL":[12345]}` puts a
 * JSON number on the wire, and only falsy values are filtered by the `if url:` guard.
 */
export const zapierTestResultSchema = z.union([
  z
    .object({
      url: uncoercedCallerValueSchema,
      success: z.literal(true),
      status_code: z.number().int(),
    })
    .strict(),
  z
    .object({
      url: uncoercedCallerValueSchema,
      success: z.literal(false),
      error: z.string(),
    })
    .strict(),
]);
export type ZapierTestResult = z.infer<typeof zapierTestResultSchema>;

/**
 * `zapierWebhookURL` is assigned verbatim at `zapier_webhook_url.py:116` with no
 * coercion and no validation — the same union as `zapierTestResult.url`. Exported
 * under its own name because it is also the write-path value.
 */
export const zapierWebhookUrlValueSchema = uncoercedCallerValueSchema;
export type ZapierWebhookUrlValue = z.infer<typeof zapierWebhookUrlValueSchema>;

/**
 * The `updated` object of the zapier write 200 — `v2_updates`. Both keys are
 * conditional (`is not None`), but the handler returns 400 "Nothing to update."
 * when BOTH are absent, so on the 200 path at least one is present. The two keys
 * differ BY CODE: `zapierConnectionEnabled` passes through `bool(...)` (really a
 * boolean); `zapierWebhookURL` is the uncoerced union.
 */
export const zapierUpdatedFieldsSchema = z
  .object({
    zapierWebhookURL: zapierWebhookUrlValueSchema.optional(),
    zapierConnectionEnabled: z.boolean().optional(),
  })
  .strict()
  .refine((u) => u.zapierWebhookURL !== undefined || u.zapierConnectionEnabled !== undefined, {
    message: 'at least one field — the both-absent case returns 400 "Nothing to update."',
  });
export type ZapierUpdatedFields = z.infer<typeof zapierUpdatedFieldsSchema>;

export const zapierTestEventBodySchema = z
  .object({
    message: z.literal('Test event(s) sent successfully.'),
    results: z.array(zapierTestResultSchema),
  })
  .strict();
export type ZapierTestEventBody = z.infer<typeof zapierTestEventBodySchema>;

export const zapierUpdatedBodySchema = z
  .object({
    message: z.literal('Merchant record updated successfully.'),
    updated: zapierUpdatedFieldsSchema,
  })
  .strict();
export type ZapierUpdatedBody = z.infer<typeof zapierUpdatedBodySchema>;
